import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Stack,
  Group,
  Select,
  Switch,
  Paper,
  Table,
  Center,
  LoadingOverlay,
  ActionIcon,
  Tooltip,
  Code,
  Loader,
  Button,
  Indicator,
  Progress,
  Menu,
  Modal,
  TextInput,
  Popover,
  ThemeIcon,
} from '@mantine/core';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  IconDatabaseOff,
  IconAdjustments,
  IconFilter,
  IconPlus,
  IconRun,
  IconRefresh,
  IconX,
  IconSettings,
  IconPlayerPlay,
  IconSearch,
  IconCalculator,
  IconDots,
  IconRefreshDot,
  IconCalculator as IconCalc,
  IconDownload,
  IconUpload,
  IconTrash,
  IconChevronDown,
  IconClock,
} from '@tabler/icons';
import { SourceIcon } from '../utils/SourceIcon';
import { FieldMappingDrawer } from './FieldMappingDrawer';
import { ExploreFieldsModal } from './ExploreFieldsModal';
import { useBagelObjects } from '../hooks/useBagelObjects';
import { useAuth0 } from '@auth0/auth0-react';
import { ExternalObjectConf, BagelObjectConf } from '@bageltechnologies/bagel-common';
import type { MappingsData } from '../../../types/integration-meta';
import { FilterPopup } from './FilterPopup';
import {
  useTriggerIntegrationSyncRun,
  useIntegrationObjectTasks,
  useIntegrationTasks,
  useUpdateIntegrationObjectMeta,
  useUnprocessedItemCount,
  useProcessUnprocessedItems,
  usePrepareIntegrationMeta,
  useUpdateExternalObject,
  useAddExternalObject,
  useDeleteExternalObject,
  useProcessUnprocessedSampleItems,
  useTriggerSyncCount,
  useFullInitialSync,
  useRemap,
  useCalculateFieldStats,
} from '../hooks/useIntegrations';
import { IntegrationSyncRunsTimeline } from './IntegrationSyncRuns/IntegrationSyncRunsTimeline';
import { RunModal } from './IntegrationSyncRuns/RunModal';
import { CountExplorationModal } from './IntegrationSyncRuns/CountExplorationModal';
import ProcessItemsModal from './IntegrationSyncRuns/ProcessItemsModal';
import { CSVUploadModal } from './CSVUploadModal';
import Papa from 'papaparse';
import { AddExternalObjectRequest } from '../api/integrations2.0';
import { resolveOriginObjectKey } from '../utils/external-object-key';

// interface BagelObject {
//   key: string;
//   label: string;
//   collection: string;
//   fields: Array<{
//     name: string;
//     fieldPath: string;
//     type: string;
//     defaultValue?: string;
//   }>;
// }

interface MappingsTabProps {
  data: MappingsData;
  isUpdatingMapping: boolean;
}

export const MappingsTab: React.FC<MappingsTabProps> = ({ data, isUpdatingMapping }) => {
  const { integrationKey, integrationName, mustSelectDateField, availableObjects } = data;
  const auth0 = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // State for external objects mapping
  const [externalObjects, setExternalObjects] = useState<ExternalObjectConf[]>([]);
  const [selectedObject, setSelectedObject] = useState<ExternalObjectConf | null>(null);
  const [selectedBagelObject, setSelectedBagelObject] = useState<BagelObjectConf | null>(null);
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});
  const [isAddMode, setIsAddMode] = useState(false);
  const [newObjectMapping, setNewObjectMapping] = useState<{
    objectId: string;
    bagelObjectKey: string;
  }>({
    objectId: '',
    bagelObjectKey: '',
  });

  // State for filter popup
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [currentFilterObject, setCurrentFilterObject] = useState<ExternalObjectConf | null>(null);

  // State for explore fields modal
  const [exploreModalOpen, setExploreModalOpen] = useState(false);
  const [exploreObjectKey, setExploreObjectKey] = useState<string | null>(null);

  // State for CSV upload modal
  const [csvUploadModalOpen, setCsvUploadModalOpen] = useState(false);
  const [csvUploadForObject, setCsvUploadForObject] = useState<ExternalObjectConf | null>(null);

  // State for delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [objectToDelete, setObjectToDelete] = useState<{
    objectKey: string;
    objectName: string;
  } | null>(null);

  // State for sync frequency modal
  const [syncFrequencyModalOpen, setSyncFrequencyModalOpen] = useState(false);
  const [currentSyncFrequencyObject, setCurrentSyncFrequencyObject] =
    useState<ExternalObjectConf | null>(null);

  // Get drawer state from URL
  const isFieldDrawerOpen = searchParams.get('drawer') === 'field-mapping';
  const selectedObjectId = searchParams.get('objectId');

  const {
    data: bagelObjects,
    isLoading: isBagelObjectsLoading,
    isError: isBagelObjectsError,
  } = useBagelObjects(auth0);

  // Hook for updating external objects via new endpoint
  const updateExternalObjectMutation = useUpdateExternalObject();
  const addExternalObjectMutation = useAddExternalObject();
  const deleteExternalObjectMutation = useDeleteExternalObject();
  const updateMeta = useUpdateIntegrationObjectMeta();

  const fullInitialSync = useFullInitialSync();
  const handleFullInitialSync = async (originObject: string) => {
    try {
      await fullInitialSync.mutateAsync({ key: integrationKey, originObjectKey: originObject });
    } catch (error) {
      console.error('Failed to full initial sync:', error);
    }
  };

  const remap = useRemap();
  const handleRemap = async (originObject: string) => {
    try {
      await remap.mutateAsync({ key: integrationKey, originObjectKey: originObject });
    } catch (error) {
      console.error('Failed to remap:', error);
    }
  };

  const calculateFieldStats = useCalculateFieldStats();
  const handleCalculateFieldStats = async (originObject: string) => {
    try {
      await calculateFieldStats.mutateAsync({ key: integrationKey, originObjectKey: originObject });
    } catch (error) {
      console.error('Failed to calculate field stats:', error);
    }
  };

  // Helper function to refresh meta for a specific object
  const refreshObjectMeta = async (originObject: string) => {
    setLoadingStates((prev) => ({ ...prev, [originObject]: true }));
    try {
      await updateMeta.mutateAsync({ key: integrationKey, object: originObject });
    } catch (e) {
      console.error('Failed to refresh meta:', e);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [originObject]: false }));
    }
  };

  // Initialize state from the configured objects (with meta fieldsMeta already injected)
  useEffect(() => {
    if (data.externalObjects) {
      setExternalObjects(data.externalObjects);
    } else {
      setExternalObjects([]);
    }
  }, [data.externalObjects]);

  // Update selected object when URL changes
  useEffect(() => {
    if (selectedObjectId && bagelObjects) {
      const externalObj = externalObjects.find(
        (obj) =>
          obj.originObjectKey === selectedObjectId ||
          obj.originObject === selectedObjectId ||
          obj.name === selectedObjectId,
      );
      if (externalObj) {
        setSelectedObject(externalObj);
        if (externalObj.bagelObjectKey) {
          const matchingBagelObject = bagelObjects.find(
            (obj: BagelObjectConf) => obj.key === externalObj.bagelObjectKey,
          );
          setSelectedBagelObject(matchingBagelObject || null);
        }
      }
    } else {
      setSelectedObject(null);
      setSelectedBagelObject(null);
    }
  }, [selectedObjectId, externalObjects, bagelObjects]);

  const handleOpenDrawer = (externalObj: ExternalObjectConf) => {
    console.log({ externalObj });
    const objectKey = externalObj.originObjectKey || externalObj.originObject || externalObj.name;
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('drawer', 'field-mapping');
    newSearchParams.set('objectId', objectKey);
    navigate(`${location.pathname}?${newSearchParams.toString()}`);
  };

  const handleCloseDrawer = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('drawer');
    newSearchParams.delete('objectId');
    navigate(`${location.pathname}?${newSearchParams.toString()}`);
  };

  const handleOpenFilterModal = (externalObj: ExternalObjectConf) => {
    setCurrentFilterObject(externalObj as ExternalObjectConf);
    setFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setFilterModalOpen(false);
    setCurrentFilterObject(null);
  };

  const handleOpenExploreModal = (externalObj: ExternalObjectConf) => {
    const objectKey = externalObj.originObjectKey || externalObj.originObject || externalObj.name;
    setExploreObjectKey(objectKey);
    setExploreModalOpen(true);
  };

  const handleCloseExploreModal = () => {
    setExploreModalOpen(false);
    setExploreObjectKey(null);
  };

  // Utility function to update a single external object
  const updateExternalObject = async (
    originObject: string,
    updates: Partial<ExternalObjectConf>,
  ): Promise<boolean> => {
    const targetIndex = externalObjects.findIndex(
      (obj) =>
        obj.originObject === originObject ||
        obj.originObjectKey === originObject ||
        obj.name === originObject,
    );
    if (targetIndex !== -1) {
      const updatedObject = { ...externalObjects[targetIndex], ...updates };
      const updatedObjects = externalObjects.map((obj, i) =>
        i === targetIndex ? updatedObject : obj,
      );
      setLoadingStates((prev) => ({ ...prev, [originObject]: true }));
      setExternalObjects(updatedObjects);

      let saved = false;
      try {
        await updateExternalObjectMutation.mutateAsync({
          key: integrationKey,
          originObjectKey: resolveOriginObjectKey(externalObjects[targetIndex]),
          updates,
        });
        saved = true;
        // Refresh meta after successful update
        await refreshObjectMeta(originObject);
      } catch (error) {
        console.error('Failed to save mapping:', error);
        if (!saved) setExternalObjects(externalObjects);
      } finally {
        setLoadingStates((prev) => ({ ...prev, [originObject]: false }));
      }
      return saved;
    }
    console.error('Object not found for originObject:', originObject);
    return false;
  };

  // Helper function to update Ongoing using new endpoint
  const updateOngoing = async (originObject: string, checked: boolean) => {
    setLoadingStates((prev) => ({ ...prev, [originObject]: true }));
    try {
      await updateExternalObjectMutation.mutateAsync({
        key: integrationKey,
        originObjectKey: originObject,
        updates: { ongoing: checked },
      });
      // Update local state on success
      setExternalObjects((prev) => {
        const idx = prev.findIndex(
          (obj) =>
            obj.originObject === originObject ||
            obj.originObjectKey === originObject ||
            obj.name === originObject,
        );
        if (idx === -1) return prev;
        return prev.map((obj, i) =>
          i === idx ? ({ ...obj, ongoing: checked } as ExternalObjectConf) : obj,
        );
      });
    } catch (error) {
      console.error('Failed to update Ongoing:', error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [originObject]: false }));
    }
  };

  // Handler to open delete confirmation modal
  const handleDeleteObject = (objectKey: string, objectName: string) => {
    setObjectToDelete({ objectKey, objectName });
    setDeleteModalOpen(true);
  };

  // Handler to confirm and execute delete
  const confirmDeleteObject = async () => {
    if (!objectToDelete) return;

    const { objectKey } = objectToDelete;
    setLoadingStates((prev) => ({ ...prev, [objectKey]: true }));

    try {
      // Remove the object from the externalObjects array
      const updatedObjects = externalObjects.filter(
        (obj) =>
          obj.originObject !== objectKey &&
          obj.originObjectKey !== objectKey &&
          obj.name !== objectKey,
      );

      await deleteExternalObjectMutation.mutateAsync({
        key: integrationKey,
        originObjectKey: objectKey,
      });
      setExternalObjects(updatedObjects);

      // Close modal and reset state
      setDeleteModalOpen(false);
      setObjectToDelete(null);
    } catch (error) {
      console.error('Failed to delete object:', error);
      // Revert state on error
      setExternalObjects(externalObjects);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [objectKey]: false }));
    }
  };

  // Helper function to get default sync date field
  const defaultSyncDate = (objectKey: string): string => {
    const externalObj = externalObjects.find(
      (obj) =>
        obj.originObject === objectKey ||
        obj.originObjectKey === objectKey ||
        obj.name === objectKey,
    );

    // Return externalObject.dateField if it exists
    if (externalObj?.dateField) {
      return externalObj.dateField;
    }

    // Find corresponding bagel object and return its dateField if it exists
    if (externalObj?.bagelObjectKey && bagelObjects) {
      const bagelObject = bagelObjects.find(
        (obj: BagelObjectConf) => obj.key === externalObj.bagelObjectKey,
      );
      if (bagelObject && (bagelObject as any).dateField) {
        return (bagelObject as any).dateField;
      }
    }

    // Fallback to createdAt
    return 'createdAt';
  };

  // Helper function to update dateField using API
  // When mustSelectDateField is true, also save rawDateField in extraParams for backend compatibility
  const handleDateFieldChange = async (originObject: string, dateField: string | null) => {
    setLoadingStates((prev) => ({ ...prev, [originObject]: true }));
    try {
      const finalDateField =
        dateField || (mustSelectDateField ? null : defaultSyncDate(originObject));

      // Build updates object - include rawDateField when mustSelectDateField is true
      const existingExternalObj = externalObjects.find(
        (obj) =>
          obj.originObject === originObject ||
          obj.originObjectKey === originObject ||
          obj.name === originObject,
      );
      const updates: Partial<ExternalObjectConf> = { dateField: finalDateField ?? null };
      if (mustSelectDateField) {
        if (finalDateField) {
          updates.extraParams = {
            ...(existingExternalObj?.extraParams ?? {}),
            rawDateField: finalDateField,
            allowNoDateList: false,
          };
        } else {
          updates.extraParams = {
            ...(existingExternalObj?.extraParams ?? {}),
            rawDateField: null,
            allowNoDateList: true,
          };
        }
      }

      await updateExternalObjectMutation.mutateAsync({
        key: integrationKey,
        originObjectKey: originObject,
        updates,
      });
      // Update local state on success
      setExternalObjects((prev) => {
        const idx = prev.findIndex(
          (obj) =>
            obj.originObject === originObject ||
            obj.originObjectKey === originObject ||
            obj.name === originObject,
        );
        if (idx === -1) return prev;
        return prev.map((obj, i) =>
          i === idx
            ? ({
                ...obj,
                dateField: finalDateField || undefined,
                ...(mustSelectDateField
                  ? finalDateField
                    ? {
                        extraParams: {
                          ...obj.extraParams,
                          rawDateField: finalDateField,
                          allowNoDateList: false,
                        },
                      }
                    : {
                        extraParams: {
                          ...obj.extraParams,
                          rawDateField: undefined,
                          allowNoDateList: true,
                        },
                      }
                  : {}),
              } as ExternalObjectConf)
            : obj,
        );
      });
    } catch (error) {
      console.error('Failed to update dateField:', error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [originObject]: false }));
    }
  };

  // Helper function to get default sync interval minutes
  const defaultSyncIntervalMinutes = (objectKey: string): number => {
    const externalObj = externalObjects.find(
      (obj) =>
        obj.originObject === objectKey ||
        obj.originObjectKey === objectKey ||
        obj.name === objectKey,
    );

    // Return externalObject.syncIntervalMinutes if it exists
    if (externalObj && (externalObj as any).syncIntervalMinutes) {
      return (externalObj as any).syncIntervalMinutes;
    }

    // Find corresponding bagel object and return its syncIntervalMinutes if it exists
    if (externalObj?.bagelObjectKey && bagelObjects) {
      const bagelObject = bagelObjects.find(
        (obj: BagelObjectConf) => obj.key === externalObj.bagelObjectKey,
      );
      if (bagelObject && (bagelObject as any).syncIntervalMinutes) {
        return (bagelObject as any).syncIntervalMinutes;
      }
    }

    // Fallback to 60 minutes (1 hour)
    return 60;
  };

  // Helper function to update syncIntervalMinutes using API
  const handleSyncIntervalChange = async (
    originObject: string,
    syncIntervalMinutes: number | null,
  ) => {
    setLoadingStates((prev) => ({ ...prev, [originObject]: true }));
    try {
      const finalSyncInterval = syncIntervalMinutes || defaultSyncIntervalMinutes(originObject);
      await updateExternalObjectMutation.mutateAsync({
        key: integrationKey,
        originObjectKey: originObject,
        updates: { syncIntervalMinutes: finalSyncInterval },
      });
      // Update local state on success
      setExternalObjects((prev) => {
        const idx = prev.findIndex(
          (obj) =>
            obj.originObject === originObject ||
            obj.originObjectKey === originObject ||
            obj.name === originObject,
        );
        if (idx === -1) return prev;
        return prev.map((obj, i) =>
          i === idx
            ? ({ ...obj, syncIntervalMinutes: finalSyncInterval } as ExternalObjectConf)
            : obj,
        );
      });
      setSyncFrequencyModalOpen(false);
      setCurrentSyncFrequencyObject(null);
    } catch (error) {
      console.error('Failed to update syncIntervalMinutes:', error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [originObject]: false }));
    }
  };

  // Handler to open sync frequency modal
  const handleOpenSyncFrequencyModal = (externalObj: ExternalObjectConf) => {
    setCurrentSyncFrequencyObject(externalObj);
    setSyncFrequencyModalOpen(true);
  };

  const handleAddNewMapping = () => {
    if (integrationKey === 'csv') {
      setCsvUploadModalOpen(true);
    } else {
      setIsAddMode(true);
    }
  };

  const handleCSVUploadSuccess = (data: any) => {
    console.log('CSV upload successful:', data);
    // TODO: Refresh the external objects or handle the uploaded data
    // You might want to trigger a refresh of the integration data here
    setCsvUploadForObject(null);
  };

  const handleUploadToObject = (externalObj: ExternalObjectConf) => {
    setCsvUploadForObject(externalObj);
    setCsvUploadModalOpen(true);
  };

  const handleCancelAddMapping = () => {
    setIsAddMode(false);
    setNewObjectMapping({
      objectId: '',
      bagelObjectKey: '',
    });
  };

  const [isAddingMapping, setIsAddingMapping] = useState(false);

  const handleSubmitNewMapping = async () => {
    console.log({ newObjectMapping });
    if (!newObjectMapping.objectId) return;

    setIsAddingMapping(true);

    const selectedMetaObject = availableObjects?.find(
      (obj: any) =>
        obj.originObjectKey === newObjectMapping.objectId ||
        obj.originObject === newObjectMapping.objectId ||
        obj.name === newObjectMapping.objectId,
    );

    if (!selectedMetaObject) {
      setIsAddingMapping(false);
      return;
    }
    const objectKey =
      selectedMetaObject.originObjectKey ||
      selectedMetaObject.originObject ||
      selectedMetaObject.name;
    // Send identity, the catalog's connector settings and the mapping choice. fieldsMeta
    // and fields belong to integrations_meta / customizations and must not travel into the
    // config; extraParams and collection do — the sync needs them to target the right
    // external resource.
    const newMapping: ExternalObjectConf = {
      originObject: objectKey,
      originObjectKey: objectKey,
      name: selectedMetaObject.name || objectKey,
      label: selectedMetaObject.label,
      description: selectedMetaObject.description,
      idField: selectedMetaObject.idField,
      dateField: selectedMetaObject.dateField,
      collection: selectedMetaObject.collection,
      extraParams: selectedMetaObject.extraParams,
      isDisabled: true,
      bagelObjectKey: newObjectMapping.bagelObjectKey,
    } as ExternalObjectConf;
    setLoadingStates((prev) => ({ ...prev, [objectKey]: true }));

    try {
      const updatedObjects = [...externalObjects, newMapping];
      await addExternalObjectMutation.mutateAsync({
        key: integrationKey,
        externalObject: newMapping as unknown as AddExternalObjectRequest,
      });
      setExternalObjects(updatedObjects);
      await refreshObjectMeta(objectKey);
      setIsAddMode(false);
      setNewObjectMapping({
        objectId: '',
        bagelObjectKey: '',
      });
    } catch (error) {
      console.error('Failed to add new mapping:', error);
    } finally {
      setIsAddingMapping(false);
      setLoadingStates((prev) => ({ ...prev, [objectKey]: false }));
    }
  };

  console.log({ externalObjects });
  const renderEmptyState = () => (
    <Box py="xl">
      <Center>
        <Stack align="center" spacing="sm">
          <IconDatabaseOff size={40} color="#CED4DA" />
          <Text size="sm" color="dimmed">
            No objects available for mapping
          </Text>
          <Text size="xs" color="dimmed">
            There are no {integrationName} objects configured for this integration
          </Text>
        </Stack>
      </Center>
    </Box>
  );

  // Replace run modal state and logic to use external objects
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [CountExplorationModalOpen, setCountExplorationModalOpen] = useState(false);
  const [currentRunObject, setCurrentRunObject] = useState<ExternalObjectConf | null>(null);
  const [currentCountObject, setCurrentCountObject] = useState<ExternalObjectConf | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isCounting, setIsCounting] = useState(false);
  const [isLoadingObjects, setIsLoadingObjects] = useState(false);
  const [selectedObjects, setSelectedObjects] = useState<string[]>([]);

  // Prepare meta hook
  const prepareMetaMutation = usePrepareIntegrationMeta();
  const handlePrepareMeta = async () => {
    try {
      const result = await prepareMetaMutation.mutateAsync(integrationKey);
      console.log('Meta preparation result:', result);
      // Optionally show success notification or update UI based on result
    } catch (error) {
      console.error('Failed to prepare meta:', error);
      // Optionally show error notification
    }
  };

  const handleOpenRunModal = (externalObj: ExternalObjectConf) => {
    setCurrentRunObject(externalObj);
    setRunModalOpen(true);
  };

  const handleOpenCountExplorationModal = (externalObj: ExternalObjectConf) => {
    setCurrentCountObject(externalObj);
    setCountExplorationModalOpen(true);
  };

  const handleCloseCountExplorationModal = () => {
    setCountExplorationModalOpen(false);
    setCurrentCountObject(null);
  };

  const handleCloseRunModal = () => {
    setRunModalOpen(false);
    setCurrentRunObject(null);
  };
  const triggerSyncRun = useTriggerIntegrationSyncRun();
  const triggerSyncCount = useTriggerSyncCount();
  const handleRun = async (dateRange?: { start: string; end: string; dateField?: string }) => {
    if (!currentRunObject) return;
    setIsRunning(true);
    const objectKey =
      currentRunObject.originObjectKey || currentRunObject.originObject || currentRunObject.name;
    await triggerSyncRun.mutateAsync({
      key: integrationKey,
      objects: [objectKey],
      dateRange,
      isSampleRun: true,
    });
    setIsRunning(false);
    handleCloseRunModal();
  };

  const handleCountExploration = async (
    dateRange?: { start: string; end: string },
    filters?: any,
    dateField?: string,
  ) => {
    if (!currentCountObject) return;
    setIsCounting(true);
    const objectKey =
      currentCountObject.originObjectKey ||
      currentCountObject.originObject ||
      currentCountObject.name;
    try {
      const filterRules = filters || { combinator: 'and', rules: [] };
      const result = await triggerSyncCount.mutateAsync({
        key: integrationKey,
        originObjectKey: objectKey,
        dateRange,
        dateField,
        filters: filterRules,
      });
      setIsCounting(false);
      return typeof result === 'number' ? result : (result?.count ?? 0);
    } catch (error) {
      console.error('Count exploration failed:', error);
      setIsCounting(false);
      throw error;
    }
  };

  // Get active tasks for all objects to show task counts
  const { data: activeTasksData } = useIntegrationTasks(
    integrationKey,
    0,
    100,
    'pending,in_progress',
  );

  // Get originId mapping status for all objects
  // Add MappingRow child component to handle per-row hooks
  const MappingRow = ({
    externalObj,
    availableKey,
    bagelObjects,
    loadingStates,
    isBagelObjectsLoading,
    isUpdatingMapping,
    updateExternalObject,
    handleOpenFilterModal,
    handleOpenExploreModal,
    handleOpenDrawer,
    handleOpenRunModal,
    handleOpenCountExplorationModal,
    activeTasks,
    mustSelectDateField,
  }: {
    externalObj: ExternalObjectConf;
    availableKey: string;
    bagelObjects: BagelObjectConf[];
    loadingStates: { [key: string]: boolean };
    isBagelObjectsLoading: boolean;
    isUpdatingMapping: boolean;
    updateExternalObject: (
      originObject: string,
      updates: Partial<ExternalObjectConf>,
    ) => Promise<boolean>;
    handleOpenFilterModal: (externalObj: ExternalObjectConf) => void;
    handleOpenExploreModal: (externalObj: ExternalObjectConf) => void;
    handleOpenDrawer: (externalObj: ExternalObjectConf) => void;
    handleOpenRunModal: (externalObj: ExternalObjectConf) => void;
    handleOpenCountExplorationModal: (externalObj: ExternalObjectConf) => void;
    activeTasks: any[];
    mustSelectDateField?: boolean;
  }) => {
    const taskCount = activeTasks.filter(
      (task: any) =>
        task.objectName === externalObj.originObjectKey ||
        task.objectName === externalObj.originObject ||
        task.objectName === externalObj.name,
    ).length;

    const [dateFieldPopoverOpened, setDateFieldPopoverOpened] = useState(false);
    const [customDateFieldValue, setCustomDateFieldValue] = useState('');

    // Build date field options from fieldsMeta when mustSelectDateField is enabled (type date or datetime)
    const dateFieldOptionsFromMeta = React.useMemo(() => {
      if (
        !mustSelectDateField ||
        !externalObj.fieldsMeta ||
        !Array.isArray(externalObj.fieldsMeta)
      ) {
        return [];
      }
      return externalObj.fieldsMeta
        .filter((f: any) => f?.type === 'date' || f?.type === 'datetime')
        .map((f: any) => ({
          value: f.fieldPath || f.name || f.key,
          label: f.label || f.name || f.key,
        }));
    }, [mustSelectDateField, externalObj.fieldsMeta]);

    // Render sync date field component
    const renderSyncDateField = () => {
      // For integrations with mustSelectDateField: use a simple Select with fields from fieldsMeta
      if (mustSelectDateField) {
        return (
          <Select
            data={[
              { value: '__none__', label: 'None (no date filter)' },
              ...dateFieldOptionsFromMeta,
            ]}
            value={externalObj.dateField || '__none__'}
            onChange={(value) => {
              handleDateFieldChange(objectKey, value === '__none__' ? null : value);
            }}
            placeholder="Select date field"
            size="xs"
            disabled={externalObj.isDisabled || loadingStates[objectKey]}
            searchable
            sx={{ minWidth: '180px' }}
            styles={{
              input: {
                opacity: externalObj.isDisabled || loadingStates[objectKey] ? 0.6 : 1,
              },
            }}
          />
        );
      }

      // For other integrations: use the existing Popover with createdAt/updatedAt
      return (
        <Popover
          opened={dateFieldPopoverOpened}
          onClose={() => {
            setDateFieldPopoverOpened(false);
            setCustomDateFieldValue('');
          }}
          position="bottom-start"
          withinPortal
          withArrow
        >
          <Popover.Target>
            <Box
              onClick={() => {
                if (!externalObj.isDisabled && !loadingStates[objectKey]) {
                  // Initialize custom value when opening
                  const currentValue = externalObj.dateField || defaultSyncDate(objectKey);
                  if (currentValue !== 'createdAt' && currentValue !== 'updatedAt') {
                    setCustomDateFieldValue(currentValue);
                  } else {
                    setCustomDateFieldValue('');
                  }
                  setDateFieldPopoverOpened((o) => !o);
                }
              }}
              sx={{
                cursor:
                  externalObj.isDisabled || loadingStates[objectKey] ? 'not-allowed' : 'pointer',
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                backgroundColor: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minWidth: '120px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor:
                    externalObj.isDisabled || loadingStates[objectKey] ? '#fff' : '#f8f9fa',
                  borderColor:
                    externalObj.isDisabled || loadingStates[objectKey] ? '#ced4da' : '#adb5bd',
                },
                '&:active': {
                  backgroundColor:
                    externalObj.isDisabled || loadingStates[objectKey] ? '#fff' : '#e9ecef',
                },
                opacity: externalObj.isDisabled || loadingStates[objectKey] ? 0.6 : 1,
              }}
            >
              <Text
                size="sm"
                color={externalObj.isDisabled || loadingStates[objectKey] ? 'dimmed' : 'inherit'}
              >
                {externalObj.dateField || defaultSyncDate(objectKey)}
              </Text>
              <IconChevronDown
                size={16}
                style={{
                  marginLeft: '8px',
                  opacity: 0.6,
                  transition: 'transform 0.2s ease',
                  transform: dateFieldPopoverOpened ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </Box>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack spacing="xs" style={{ minWidth: '250px' }}>
              <TextInput
                placeholder="Custom date field (e.g., fields.customDate)"
                value={customDateFieldValue}
                onChange={(event) => {
                  setCustomDateFieldValue(event.currentTarget.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    const value = event.currentTarget.value.trim();
                    if (value) {
                      handleDateFieldChange(objectKey, value);
                      setDateFieldPopoverOpened(false);
                      setCustomDateFieldValue('');
                    }
                  }
                }}
                size="xs"
              />
              <Select
                data={[
                  { label: 'Created At', value: 'createdAt' },
                  { label: 'Updated At', value: 'updatedAt' },
                ]}
                value={
                  externalObj.dateField === 'createdAt' || externalObj.dateField === 'updatedAt'
                    ? externalObj.dateField
                    : defaultSyncDate(objectKey) === 'createdAt' ||
                        defaultSyncDate(objectKey) === 'updatedAt'
                      ? defaultSyncDate(objectKey)
                      : null
                }
                onChange={(value) => {
                  if (value) {
                    handleDateFieldChange(objectKey, value);
                    setDateFieldPopoverOpened(false);
                  }
                }}
                placeholder="Select date field"
                size="xs"
              />
            </Stack>
          </Popover.Dropdown>
        </Popover>
      );
    };

    // Use the top-level refreshObjectMeta function (no duplication)

    // Process Items Modal state and hooks
    const [processModalOpen, setProcessModalOpen] = useState(false);
    const {
      data: processCounts,
      isLoading: isCountsLoading,
      isError: isCountsError,
      error: countsError,
      refetch: refetchCounts,
    } = useUnprocessedItemCount(availableKey, externalObj.originObject || '');
    const processMutation = useProcessUnprocessedItems();
    const processSamplesMutation = useProcessUnprocessedSampleItems();
    const [processError, setProcessError] = useState<string | null>(null);

    const handleOpenProcessModal = () => {
      setProcessError(null);
      setProcessModalOpen(true);
      refetchCounts();
    };
    const handleCloseProcessModal = () => {
      setProcessModalOpen(false);
    };
    const handleProcess = async () => {
      setProcessError(null);
      try {
        const objectKey =
          externalObj.originObjectKey || externalObj.originObject || externalObj.name;
        await processSamplesMutation.mutateAsync({ key: availableKey, originObjectKey: objectKey });
        refetchCounts();
      } catch (e: any) {
        setProcessError(e?.message || 'Failed to process items');
      }
    };

    const handleDownloadCSVTemplate = () => {
      // Extract fields from fieldsMeta
      const fields = externalObj.fieldsMeta || [];
      if (fields.length === 0) {
        console.warn('No fieldsMeta available for this object');
        return;
      }

      // Use field keys as CSV column headers
      const headers = fields
        .map((field: any) => field.key || field.name || field.label || '')
        .filter(Boolean);

      // Generate empty CSV (headers only, no data rows)
      const csvData = [headers];
      const csv = Papa.unparse(csvData);

      // Create download link
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      const filename = `${externalObj.name || externalObj.label || 'template'}_template.csv`;
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    const objectKey = externalObj.originObjectKey || externalObj.originObject || externalObj.name;
    return (
      <tr key={objectKey} style={{ opacity: loadingStates[objectKey] ? 0.5 : 1 }}>
        <td>
          <Group spacing="sm">
            {loadingStates[objectKey] ? (
              <Loader size={20} />
            ) : (
              <SourceIcon sourceName={availableKey} width={20} height={20} />
            )}
            <Box>
              <Text>{externalObj.label}</Text>
              {externalObj.label !== objectKey && (
                <Text size="xs" color="dimmed">
                  {objectKey}{' '}
                  {externalObj.extraParams?.projectKey &&
                    `[${externalObj.extraParams?.projectKey}]`}
                </Text>
              )}
            </Box>
          </Group>
        </td>
        <td>
          <Switch
            checked={externalObj.isDisabled !== true}
            onChange={(event) =>
              updateExternalObject(objectKey, { isDisabled: !event.currentTarget.checked })
            }
            disabled={loadingStates[objectKey] || isBagelObjectsLoading}
          />
        </td>
        <td>
          <Select
            data={
              bagelObjects?.map((obj) => ({
                label: obj.label,
                value: obj.key,
              })) || []
            }
            value={externalObj.bagelObjectKey || ''}
            onChange={(value) => updateExternalObject(objectKey, { bagelObjectKey: value || '' })}
            placeholder="Select object"
            disabled={
              externalObj.isDisabled === true || loadingStates[objectKey] || isBagelObjectsLoading
            }
          />
        </td>
        <td style={{ position: 'relative', overflow: 'visible' }}>{renderSyncDateField()}</td>
        <td>
          <Tooltip label="Count exploration">
            <ActionIcon
              variant="outline"
              color="teal"
              onClick={() => handleOpenCountExplorationModal(externalObj)}
              disabled={externalObj.isDisabled === true}
            >
              <IconCalculator size={16} />
            </ActionIcon>
          </Tooltip>
        </td>
        {/* Refresh Meta Column */}
        <td>
          <Tooltip label="Refresh object meta">
            <ActionIcon
              variant="outline"
              color="gray"
              onClick={() => refreshObjectMeta(objectKey)}
              disabled={
                externalObj.isDisabled === true || loadingStates[objectKey] || isUpdatingMapping
              }
            >
              {loadingStates[objectKey] ? (
                <Loader size={16} />
              ) : (
                <IconRefresh size={16} className="icon-rotate-2" />
              )}
            </ActionIcon>
          </Tooltip>
        </td>
        {availableKey !== 'api' && (
          <td>
            <Tooltip label="Get samples">
              <ActionIcon
                variant="outline"
                color="teal"
                onClick={() => handleOpenRunModal(externalObj)}
                disabled={
                  externalObj.isDisabled === true || loadingStates[objectKey] || isUpdatingMapping
                }
              >
                <Indicator
                  disabled={taskCount === 0}
                  processing
                  size={16}
                  fz={8}
                  color="teal"
                  label={taskCount}
                >
                  <IconRun size={16} />
                </Indicator>
              </ActionIcon>
            </Tooltip>
          </td>
        )}
        {/* Explore Fields Column */}
        <td>
          <Tooltip label="Explore fields">
            <ActionIcon
              variant="outline"
              color="blue"
              onClick={() => handleOpenExploreModal(externalObj)}
              disabled={
                externalObj.isDisabled === true ||
                loadingStates[objectKey] ||
                isUpdatingMapping ||
                !externalObj.fieldsMeta?.length
              }
            >
              <IconSearch size={16} />
            </ActionIcon>
          </Tooltip>
        </td>
        {/* New Process Items column */}
        <td>
          <Tooltip label="Process items">
            <ActionIcon
              variant="outline"
              color={
                processCounts?.unprocessed === 0 && (processCounts?.processed || 0) > 0
                  ? 'green' // All processed
                  : (processCounts?.unprocessed || 0) > 0
                    ? 'orange' // Some unprocessed
                    : 'gray' // No items or loading
              }
              onClick={handleOpenProcessModal}
              disabled={
                externalObj.isDisabled === true || loadingStates[objectKey] || isUpdatingMapping
              }
            >
              <IconPlayerPlay size={16} />
            </ActionIcon>
          </Tooltip>
          <ProcessItemsModal
            opened={processModalOpen}
            onClose={handleCloseProcessModal}
            processed={processCounts?.processed || 0}
            unprocessed={processCounts?.unprocessed || 0}
            isProcessing={processMutation.isPending}
            onProcess={handleProcess}
            error={processError || (isCountsError ? (countsError as any)?.message : null)}
          />
        </td>
        <td>
          <Tooltip label="Configure filters">
            <ActionIcon
              variant="outline"
              color="indigo"
              onClick={() => handleOpenFilterModal(externalObj)}
              disabled={
                externalObj.isDisabled === true || loadingStates[objectKey] || isUpdatingMapping
              }
            >
              <Indicator
                processing
                disabled={Boolean(!externalObj?.filters?.rules?.length)}
                size={16}
                fz={8}
                color="indigo"
                label={externalObj?.filters?.rules?.length || 0}
              >
                <IconFilter size={16} />
              </Indicator>
            </ActionIcon>
          </Tooltip>
        </td>
        {/* {integrationKey !== 'api' && integrationKey !== 'csv' && (
          <td>
            <Tooltip label="All configurations are done let's sync">
              <Switch
                checked={(externalObj as any).readyToSync === true}
                onChange={(event) => updateReadyToSync(objectKey, event.currentTarget.checked)}
                disabled={externalObj.isDisabled === true || loadingStates[objectKey] || isBagelObjectsLoading || !externalObj.bagelObjectKey}
              />
            </Tooltip>
          </td>
        )} */}
        <td>
          <Tooltip label="Ready to process items">
            <Switch
              checked={(externalObj as any).ongoing === true}
              onChange={(event) => updateOngoing(objectKey, event.currentTarget.checked)}
              disabled={
                externalObj.isDisabled === true ||
                loadingStates[objectKey] ||
                isBagelObjectsLoading ||
                !externalObj.bagelObjectKey
              }
            />
          </Tooltip>
        </td>
        <td>
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <Button
                variant="filled"
                size="xs"
                leftIcon={<IconDots size={16} />}
                disabled={
                  externalObj.isDisabled === true ||
                  loadingStates[objectKey] ||
                  isBagelObjectsLoading
                }
                sx={{
                  backgroundColor: '#5C5CEB',
                  color: 'white',
                  fontSize: 12,
                  fontWeight: 500,
                  ':hover': {
                    backgroundColor: '#4C4CDB',
                  },
                }}
              >
                Actions
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                icon={<IconRefreshDot size={16} />}
                onClick={() => handleFullInitialSync(objectKey)}
                disabled={!externalObj.bagelObjectKey}
              >
                Full Initial Sync
              </Menu.Item>
              <Menu.Item
                icon={<IconRefresh size={16} />}
                onClick={() => handleRemap(objectKey)}
                disabled={!externalObj.bagelObjectKey}
              >
                ReProcess All orignal items (Remap)
              </Menu.Item>
              <Menu.Item
                icon={<IconCalc size={16} />}
                onClick={() => handleCalculateFieldStats(objectKey)}
                disabled={!externalObj.bagelObjectKey}
              >
                Calculate Fields Stats
              </Menu.Item>
              <Menu.Item
                icon={<IconClock size={16} />}
                onClick={() => handleOpenSyncFrequencyModal(externalObj)}
              >
                Set Sync Frequency
              </Menu.Item>
              {/* admin: delete gated — /admin/integration-objects has no DELETE route yet.
                  Use the per-row Disable toggle instead. */}
              {false && (
                <>
                  <Menu.Divider />
                  <Menu.Item
                    icon={<IconTrash size={16} />}
                    color="red"
                    onClick={() =>
                      handleDeleteObject(objectKey, externalObj.label || externalObj.name)
                    }
                  >
                    Delete
                  </Menu.Item>
                </>
              )}
            </Menu.Dropdown>
          </Menu>
        </td>
        {availableKey === 'csv' && (
          <td>
            <Group spacing="xs">
              <Tooltip label="Download current structure">
                <ActionIcon
                  variant="outline"
                  color="blue"
                  onClick={handleDownloadCSVTemplate}
                  disabled={
                    externalObj.isDisabled === true ||
                    loadingStates[objectKey] ||
                    !externalObj.fieldsMeta ||
                    externalObj.fieldsMeta.length === 0
                  }
                >
                  <IconDownload size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Upload to this object">
                <ActionIcon
                  variant="outline"
                  color="green"
                  onClick={() => handleUploadToObject(externalObj)}
                  disabled={externalObj.isDisabled === true || loadingStates[objectKey]}
                >
                  <IconUpload size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </td>
        )}
        <td style={{ justifyItems: 'right' }}>
          <Tooltip label="Map fields">
            <ActionIcon
              variant="outline"
              color="indigo"
              disabled={
                !(externalObj.bagelObjectKey && externalObj.isDisabled !== true) ||
                loadingStates[objectKey] ||
                isBagelObjectsLoading
              }
              onClick={() => handleOpenDrawer(externalObj)}
            >
              <IconAdjustments size={16} />
            </ActionIcon>
          </Tooltip>
        </td>
      </tr>
    );
  };

  return (
    <Box pos="relative">
      <FieldMappingDrawer
        opened={isFieldDrawerOpen}
        onClose={handleCloseDrawer}
        selectedObject={selectedObject}
        selectedBagelObject={selectedBagelObject}
        integrationKey={integrationKey}
        integrationName={integrationName}
        externalObjects={externalObjects}
        updateExternalObject={updateExternalObject}
      />

      {/* Filter Modal */}
      {currentFilterObject && (
        <FilterPopup
          opened={filterModalOpen}
          onClose={handleCloseFilterModal}
          externalObj={currentFilterObject}
          integrationKey={integrationKey}
        />
      )}

      {/* Explore Fields Modal */}
      <ExploreFieldsModal
        opened={exploreModalOpen}
        onClose={handleCloseExploreModal}
        externalObject={
          exploreObjectKey
            ? externalObjects.find(
                (obj) =>
                  obj.originObjectKey === exploreObjectKey ||
                  obj.originObject === exploreObjectKey ||
                  obj.name === exploreObjectKey,
              ) || null
            : null
        }
        integrationName={integrationName}
        integrationKey={integrationKey}
      />
      <Stack spacing="xl">
        <Box>
          <Group position="apart" align="flex-start">
            <Box>
              <Text weight={500} size="md">
                Object Mappings
              </Text>
              <Text size="sm" color="dimmed" mb="xs">
                Configure how your {integrationName} data syncs with Bagel by mapping objects
                between the systems.
              </Text>
              {externalObjects.length > 0 && (
                <Text size="sm" color="dimmed">
                  For instance, you can map {integrationName}{' '}
                  <Code>{externalObjects[0].label}</Code> to a Bagel object.
                </Text>
              )}
            </Box>
            <Button
              leftIcon={<IconRefresh size={16} />}
              variant="subtle"
              color="indigo"
              onClick={handlePrepareMeta}
              loading={prepareMetaMutation.isPending}
              size="sm"
            >
              Prepare Meta
            </Button>
          </Group>
        </Box>

        <Paper withBorder sx={{ overflowX: 'auto' }}>
          <Table striped highlightOnHover sx={{ minWidth: 1200 }}>
            <thead>
              <tr>
                <th>{integrationName} Objects</th>
                <th>Enable</th>
                <th>Bagel Objects</th>
                <th>Sync Date</th>
                <th>Count</th>
                <th>Refresh</th>
                {integrationKey !== 'api' && <th>Get samples</th>}
                <th>Explore</th>
                <th>Process Samples</th>
                <th>Filter</th>
                {/* {integrationKey !== 'api' && integrationKey !== 'csv' && <th>Full Import</th>} */}
                <th>Ongoing</th>
                <th>Actions</th>
                {integrationKey === 'csv' && <th>CSV Actions</th>}
                <th style={{ textAlign: 'right' }}>Configure</th>
              </tr>
            </thead>
            <tbody>
              {externalObjects.length === 0 ? (
                <tr>
                  <td
                    colSpan={integrationKey === 'api' ? 10 : integrationKey === 'csv' ? 11 : 11}
                    style={{ padding: 0, border: 'none' }}
                  >
                    {renderEmptyState()}
                  </td>
                </tr>
              ) : (
                externalObjects.map((externalObj) => (
                  <MappingRow
                    key={
                      externalObj.originObjectKey || externalObj.originObject || externalObj.name
                    }
                    externalObj={externalObj}
                    availableKey={integrationKey}
                    bagelObjects={bagelObjects}
                    loadingStates={loadingStates}
                    isBagelObjectsLoading={isBagelObjectsLoading}
                    isUpdatingMapping={isUpdatingMapping}
                    updateExternalObject={updateExternalObject}
                    handleOpenFilterModal={handleOpenFilterModal}
                    handleOpenExploreModal={handleOpenExploreModal}
                    handleOpenDrawer={handleOpenDrawer}
                    handleOpenRunModal={handleOpenRunModal}
                    handleOpenCountExplorationModal={handleOpenCountExplorationModal}
                    activeTasks={activeTasksData?.tasks || []}
                    mustSelectDateField={mustSelectDateField}
                  />
                ))
              )}
              {isAddMode && (
                <tr>
                  <td>
                    <Group spacing="sm" noWrap>
                      <SourceIcon sourceName={integrationKey} width={20} height={20} />
                      <Select
                        itemComponent={({
                          label,
                          value,
                          projectKey,
                          originObject,
                          originObjectKey,
                          name,
                          ...rest
                        }: any) => (
                          <Box {...rest}>
                            <Text size="sm" weight={500}>
                              {name}
                            </Text>
                            <Group spacing="xs" mt={4} noWrap>
                              {(originObjectKey || originObject) &&
                                (originObjectKey || originObject) !== name && (
                                  <Code color="gray">{originObjectKey || originObject}</Code>
                                )}
                              {projectKey && (
                                <Code color="blue">
                                  <Group spacing={4}>
                                    <SourceIcon
                                      sourceName={integrationKey}
                                      width={10}
                                      height={10}
                                    />
                                    {projectKey}
                                  </Group>
                                </Code>
                              )}
                            </Group>
                          </Box>
                        )}
                        data={
                          availableObjects?.map(
                            (obj: {
                              id: string;
                              name?: string;
                              originObject?: string;
                              originObjectKey?: string;
                              extraParams?: { projectKey?: string };
                            }) => {
                              const projectKey = obj.extraParams?.projectKey;
                              let label = obj.name || '';

                              // Add originObject if different from name
                              if (
                                (obj.originObjectKey || obj.originObject) &&
                                (obj.originObjectKey || obj.originObject) !== obj.name
                              ) {
                                label += ` (${obj.originObject})`;
                              }

                              // Add projectKey if available
                              if (projectKey) {
                                label += ` [${projectKey}]`;
                              }

                              return {
                                label,
                                value: obj.originObject || obj.name,
                                disabled: externalObjects.some(
                                  (extObj) =>
                                    extObj.originObjectKey ===
                                      (obj.originObjectKey || obj.originObject) ||
                                    extObj.originObject === (obj.originObject || obj.name),
                                ),
                                projectKey, // Store for search and display
                                originObject: obj.originObjectKey || obj.originObject,
                                name: obj.name,
                              };
                            },
                          ) || []
                        }
                        value={newObjectMapping.objectId}
                        onChange={(value) =>
                          setNewObjectMapping((prev) => ({ ...prev, objectId: value || '' }))
                        }
                        placeholder="Select object"
                        style={{ minWidth: 200 }}
                        searchable
                        nothingFound="No objects found"
                        filter={(value, item) => {
                          const searchValue = value.toLowerCase();
                          return (
                            item.label?.toLowerCase().includes(searchValue) ||
                            (item.value as string)?.toLowerCase().includes(searchValue) ||
                            (item as any).projectKey?.toLowerCase().includes(searchValue) ||
                            (item as any).name?.toLowerCase().includes(searchValue)
                          );
                        }}
                      />
                    </Group>
                  </td>
                  <td>
                    <Switch checked={false} disabled />
                  </td>
                  <td>
                    <ActionIcon variant="outline" color="blue" disabled>
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </td>
                  <td>
                    <ActionIcon variant="outline" color="blue" disabled>
                      <IconSearch size={16} />
                    </ActionIcon>
                  </td>
                  {integrationKey !== 'api' && (
                    <td>
                      <ActionIcon variant="outline" color="teal" disabled>
                        <IconRun size={16} />
                      </ActionIcon>
                    </td>
                  )}
                  <td>
                    <ActionIcon variant="outline" color="gray" disabled>
                      <IconPlayerPlay size={16} />
                    </ActionIcon>
                  </td>
                  <td>
                    <ActionIcon variant="outline" color="blue" disabled>
                      <IconFilter size={16} />
                    </ActionIcon>
                  </td>
                  <td>
                    <Select
                      data={
                        bagelObjects?.map((obj: BagelObjectConf) => ({
                          label: obj.label,
                          value: obj.key,
                        })) || []
                      }
                      value={newObjectMapping.bagelObjectKey}
                      onChange={(value) =>
                        setNewObjectMapping((prev) => ({ ...prev, bagelObjectKey: value || '' }))
                      }
                      placeholder="Select object"
                    />
                  </td>
                  <td>
                    <Switch checked={false} disabled />
                  </td>
                  <td>
                    <Switch checked={false} disabled />
                  </td>
                  <td style={{ justifyItems: 'right' }}>
                    <Group spacing="xs">
                      <Button
                        size="xs"
                        color="indigo"
                        onClick={handleSubmitNewMapping}
                        disabled={!newObjectMapping.objectId}
                        rightIcon={isAddingMapping ? <Loader size={14} color="white" /> : undefined}
                      >
                        Add
                      </Button>
                      <ActionIcon variant="outline" color="gray" onClick={handleCancelAddMapping}>
                        <IconX size={16} />
                      </ActionIcon>
                    </Group>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Paper>
        {!isAddMode &&
          integrationKey !== 'apollo' &&
          integrationKey !== 'api' &&
          // admin: gated unless the origin-object catalog is present (no admin endpoint for it yet)
          (availableObjects?.length ?? 0) > 0 && (
            <Button
              leftIcon={<IconPlus size={16} />}
              onClick={handleAddNewMapping}
              variant="outline"
            >
              {externalObjects.length === 0
                ? 'Add new object'
                : `Sync another ${integrationName} object to Bagel`}
            </Button>
          )}
      </Stack>
      {/* Run Modal Popup (moved to IntegrationSyncRunsTimeline for reusability) */}
      <RunModal
        opened={runModalOpen}
        onClose={handleCloseRunModal}
        externalObjects={externalObjects}
        externalObject={currentRunObject}
        isLoadingObject={isLoadingObjects}
        selectedObjects={selectedObjects}
        setSelectedObjects={setSelectedObjects}
        onRun={handleRun}
        isRunning={isRunning}
        integrationKey={integrationKey}
        activeTasks={(activeTasksData?.tasks || []).filter((task: any) =>
          currentRunObject
            ? task.objectName === currentRunObject.originObjectKey ||
              task.objectName === currentRunObject.originObject ||
              task.objectName === currentRunObject.name
            : true,
        )}
      />

      {/* Count Exploration Modal */}
      <CountExplorationModal
        opened={CountExplorationModalOpen}
        onClose={handleCloseCountExplorationModal}
        externalObject={currentCountObject}
        isLoadingObject={isLoadingObjects}
        onCount={handleCountExploration}
        isCounting={isCounting}
        integrationKey={integrationKey}
      />

      {/* CSV Upload Modal */}
      <CSVUploadModal
        opened={csvUploadModalOpen}
        onClose={() => {
          setCsvUploadModalOpen(false);
          setCsvUploadForObject(null);
        }}
        integrationKey={integrationKey}
        onUploadSuccess={handleCSVUploadSuccess}
        prefilledData={
          csvUploadForObject
            ? {
                feedbackName: csvUploadForObject.name || csvUploadForObject.label || '',
                feedbackCategory: csvUploadForObject.bagelObjectKey || '',
                originSystem: 'csv',
              }
            : undefined
        }
        disableMetadataFields={!!csvUploadForObject}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setObjectToDelete(null);
        }}
        title="Delete Object"
        centered
      >
        <Text>
          Are you sure you want to delete <strong>{objectToDelete?.objectName}</strong>? This action
          cannot be undone.
        </Text>
        <Group position="right" mt="xl">
          <Button
            variant="outline"
            onClick={() => {
              setDeleteModalOpen(false);
              setObjectToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={confirmDeleteObject}
            loading={objectToDelete ? loadingStates[objectToDelete.objectKey] : false}
          >
            Delete
          </Button>
        </Group>
      </Modal>

      {/* Sync Frequency Modal */}
      <Modal
        opened={syncFrequencyModalOpen}
        onClose={() => {
          setSyncFrequencyModalOpen(false);
          setCurrentSyncFrequencyObject(null);
        }}
        title="Set Sync Frequency"
        centered
      >
        {currentSyncFrequencyObject && (
          <Stack spacing="md">
            <Text size="sm" color="dimmed">
              Select the sync frequency for{' '}
              <strong>{currentSyncFrequencyObject.label || currentSyncFrequencyObject.name}</strong>
            </Text>
            <Select
              label="Sync Interval"
              placeholder="Select sync interval"
              data={[
                { value: '10', label: '10 minutes' },
                { value: '60', label: '1 hour' },
                { value: '1440', label: '24 hours' },
              ]}
              value={
                currentSyncFrequencyObject &&
                (currentSyncFrequencyObject as any).syncIntervalMinutes
                  ? String((currentSyncFrequencyObject as any).syncIntervalMinutes)
                  : String(
                      defaultSyncIntervalMinutes(
                        currentSyncFrequencyObject.originObject ||
                          currentSyncFrequencyObject.name ||
                          '',
                      ),
                    )
              }
              onChange={(value) => {
                if (value && currentSyncFrequencyObject) {
                  const objectKey =
                    currentSyncFrequencyObject.originObject ||
                    currentSyncFrequencyObject.name ||
                    '';
                  handleSyncIntervalChange(objectKey, parseInt(value, 10));
                }
              }}
              withinPortal
            />
            <Group position="right" mt="xl">
              <Button
                variant="outline"
                onClick={() => {
                  setSyncFrequencyModalOpen(false);
                  setCurrentSyncFrequencyObject(null);
                }}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Box>
  );
};

export default MappingsTab;
